import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { SilaeFormData, StructureType } from "@/types/silae";
import { createEmptyBranch } from "@/hooks/useSilaeWizardFormik";

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_WIZARD_STEPS = 6; // Nombre maximum d'étapes dans le wizard (structure if_else = 6 étapes)

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Crée un état initial pour un nouveau formulaire
 */
const createInitialFormData = (): SilaeFormData => ({
  structureType: "simple",
  hasExclusions: false,
  exclusions: undefined,
  exclusionsOperators: [],
  conditions: [],
  conditionsOperators: [],
  ifBranch: createEmptyBranch(),
  elseBranch: undefined,
  comment: "",
});

/**
 * Valide les données du formulaire
 */
const validateFormData = (formData: SilaeFormData): boolean => {
  if (!formData) return false;
  if (!formData.structureType) return false;
  if (!formData.ifBranch) return false;
  return true;
};

/**
 * Valide le numéro d'étape
 */
const validateStep = (step: number): boolean => {
  return Number.isInteger(step) && step >= 0 && step < MAX_WIZARD_STEPS;
};

/**
 * Nettoie les données du formulaire en fonction du type de structure
 */
const sanitizeFormData = (formData: SilaeFormData): SilaeFormData => {
  const sanitized = { ...formData };

  // Si structure simple, nettoyer les conditions et exclusions
  if (sanitized.structureType === "simple") {
    sanitized.conditions = [];
    sanitized.conditionsOperators = [];
    sanitized.elseBranch = undefined;
  }

  // Si pas d'exclusions, nettoyer les données d'exclusion
  if (!sanitized.hasExclusions) {
    sanitized.exclusions = undefined;
    sanitized.exclusionsOperators = [];
  }

  return sanitized;
};

// ============================================================================
// TYPES
// ============================================================================

interface CodeGenerationState {
  formData: SilaeFormData;
  generatedCode: string;
  currentStep: number;
  isModified: boolean; // Indique si le formulaire a été modifié
}

const initialState: CodeGenerationState = {
  formData: createInitialFormData(),
  generatedCode: "",
  currentStep: 0,
  isModified: false,
};

// ============================================================================
// SLICE
// ============================================================================

const codeGenerationSlice = createSlice({
  name: "codeGeneration",
  initialState,
  reducers: {
    /**
     * Remplace complètement les données du formulaire
     */
    setFormData: (state, action: PayloadAction<SilaeFormData>) => {
      if (!validateFormData(action.payload)) {
        console.error("Invalid form data:", action.payload);
        return;
      }

      state.formData = sanitizeFormData(action.payload);
      state.isModified = true;
    },

    /**
     * Met à jour partiellement les données du formulaire
     */
    updateFormData: (state, action: PayloadAction<Partial<SilaeFormData>>) => {
      const updatedFormData = { ...state.formData, ...action.payload };

      if (!validateFormData(updatedFormData)) {
        console.error("Invalid form data after update:", updatedFormData);
        return;
      }

      state.formData = sanitizeFormData(updatedFormData);
      state.isModified = true;
    },

    /**
     * Met à jour le type de structure et nettoie les données incompatibles
     */
    setStructureType: (state, action: PayloadAction<StructureType>) => {
      state.formData.structureType = action.payload;
      state.formData = sanitizeFormData(state.formData);
      state.isModified = true;
    },

    /**
     * Définit le code généré
     */
    setGeneratedCode: (state, action: PayloadAction<string>) => {
      state.generatedCode = action.payload;
    },

    /**
     * Change l'étape du wizard
     */
    setCurrentStep: (state, action: PayloadAction<number>) => {
      if (!validateStep(action.payload)) {
        console.error("Invalid step number:", action.payload);
        return;
      }
      state.currentStep = action.payload;
    },

    /**
     * Passe à l'étape suivante
     */
    nextStep: (state) => {
      if (state.currentStep < MAX_WIZARD_STEPS - 1) {
        state.currentStep += 1;
      }
    },

    /**
     * Retourne à l'étape précédente
     */
    previousStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },

    /**
     * Réinitialise complètement le formulaire
     */
    resetForm: (state) => {
      state.formData = createInitialFormData();
      state.generatedCode = "";
      state.currentStep = 0;
      state.isModified = false;
    },

    /**
     * Marque le formulaire comme non modifié (après sauvegarde)
     */
    markAsSaved: (state) => {
      state.isModified = false;
    },

    /**
     * Met à jour le commentaire libre du diagramme.
     * Tronque silencieusement au-delà de 1500 caractères.
     */
    setComment: (state, action: PayloadAction<string>) => {
      const next = (action.payload ?? "").slice(0, 1500);
      if (state.formData.comment === next) return;
      state.formData.comment = next;
      state.isModified = true;
    },
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  setFormData,
  updateFormData,
  setStructureType,
  setGeneratedCode,
  setCurrentStep,
  nextStep,
  previousStep,
  resetForm,
  markAsSaved,
  setComment,
} = codeGenerationSlice.actions;

export default codeGenerationSlice.reducer;
